import React, { useState, useRef, useCallback } from "react";
import { showErrorAlert } from "../utils/alertUtils";
export interface Attachment {
  id?: number | string;
  name: string;
  size?: number;
  type?: string;
  url?: string;
  created_at?: string;
  created_by?: number;
}
export interface AttachmentComponentProps {
  allowUpload?: boolean;
  maxFiles?: number;
  maxFileSize?: number;
  acceptedFileTypes?: string[];
  onFileUpload?: (files: File[]) => void;
  onFileRemove?: (index: number) => void;
  attachments: (File | Attachment)[];
  showFileSize?: boolean;
  showUploadDate?: boolean;
  showActions?: boolean;
  className?: string;
  uploadButtonText?: string;
  dragDropText?: string;
  noAttachmentsText?: string;
  onDownload?: (attachment: File | Attachment) => void;
  onDelete?: (attachment: File | Attachment, index: number) => void;
  onPreview?: (attachment: File | Attachment) => void;
  variant?: "default" | "compact" | "detailed";
  theme?: "light" | "dark";
}
const AttachmentComponent: React.FC<AttachmentComponentProps> = ({
  allowUpload = true,
  maxFiles = 5,
  maxFileSize = 10 * 1024 * 1024,
  acceptedFileTypes = [
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".bmp",
    ".pdf",
    ".doc",
    ".docx",
    ".xls",
    ".xlsx",
    ".ppt",
    ".pptx",
    ".txt",
    ".zip",
    ".rar",
  ],
  onFileUpload,
  onFileRemove,
  attachments = [],
  showFileSize = true,
  showUploadDate = true,
  showActions = true,
  className = "",
  uploadButtonText = "Upload Files",
  dragDropText = "Drag & drop files here or click to browse",
  noAttachmentsText = "No attachments",
  onDownload,
  onDelete,
  onPreview,
  variant = "default",
  theme = "light",
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFileSelect = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const fileArray = Array.from(files);
      const validFiles: File[] = [];
      const validationErrors: string[] = [];
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      fileArray.forEach((file) => {
        if (file.size > maxFileSize) {
          validationErrors.push(
            `${file.name} is too large. Maximum size is ${(
              maxFileSize /
              (1024 * 1024)
            ).toFixed(1)}MB.`
          );
          return;
        }
        const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
        if (!acceptedFileTypes.includes(fileExtension)) {
          validationErrors.push(
            `${
              file.name
            } has an unsupported file type. Allowed types: ${acceptedFileTypes.join(
              ", "
            )}`
          );
          return;
        }
        validFiles.push(file);
      });
      if (validationErrors.length > 0) {
        const errorMessage = validationErrors.join("\n");
        setTimeout(() => {
          showErrorAlert(errorMessage);
        }, 100);
      }
      if (validFiles.length === 0) return;
      const currentTotalFiles = attachments.length;
      if (currentTotalFiles >= maxFiles) {
        setTimeout(() => {
          showErrorAlert(`Maximum file limit reached! You can only upload up to ${maxFiles} files.`);
        }, 100);
        return;
      }
      const remainingSlots = maxFiles - currentTotalFiles;
      const filesToAdd = Math.min(validFiles.length, remainingSlots);
      const acceptedFiles = validFiles.slice(0, filesToAdd);
      if (onFileUpload) {
        onFileUpload(acceptedFiles);
      }
      if (validFiles.length > remainingSlots) {
        setTimeout(() => {
          showErrorAlert(`You can only add ${remainingSlots} more file(s). You currently have ${currentTotalFiles} files.`);
        }, 100);
      }
      setSelectedFiles((prev) => [...prev, ...acceptedFiles]);
    },
    [attachments.length, maxFiles, maxFileSize, acceptedFileTypes, onFileUpload]
  );
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect]
  );
  const removeSelectedFile = useCallback(
    (index: number) => {
      setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
      if (onFileRemove) {
        onFileRemove(index);
      }
    },
    [onFileRemove]
  );
  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }, []);
  const getFileIcon = useCallback(
    (fileName: string, fileType?: string): string => {
      const extension = fileName.split(".").pop()?.toLowerCase();
      if (
        fileType?.startsWith("image/") ||
        ["jpg", "jpeg", "png", "gif", "bmp"].includes(extension || "")
      ) {
        return "ti ti-photo";
      } else if (["pdf"].includes(extension || "")) {
        return "ti ti-file-text";
      } else if (["doc", "docx"].includes(extension || "")) {
        return "ti ti-file-text";
      } else if (["xls", "xlsx"].includes(extension || "")) {
        return "ti ti-table";
      } else if (["ppt", "pptx"].includes(extension || "")) {
        return "ti ti-presentation";
      } else if (["zip", "rar"].includes(extension || "")) {
        return "ti ti-archive";
      } else {
        return "ti ti-file";
      }
    },
    []
  );
  const isFileObject = (attachment: File | Attachment): attachment is File => {
    return attachment instanceof File;
  };
  const getFileName = (attachment: File | Attachment): string => {
    if (isFileObject(attachment)) {
      return attachment.name;
    }
    return attachment.name;
  };
  const getFileSize = (attachment: File | Attachment): number => {
    if (isFileObject(attachment)) {
      return attachment.size;
    }
    return attachment.size || 0;
  };
  const getFileType = (attachment: File | Attachment): string => {
    if (isFileObject(attachment)) {
      return attachment.type;
    }
    return attachment.type || "";
  };
  const handleDownload = useCallback(
    (attachment: File | Attachment) => {
      if (onDownload) {
        onDownload(attachment);
      } else if (isFileObject(attachment)) {
        const url = URL.createObjectURL(attachment);
        const a = document.createElement("a");
        a.href = url;
        a.download = attachment.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else if (attachment.url) {
        const a = document.createElement("a");
        a.href = attachment.url;
        a.download = attachment.name;
        a.target = "_blank";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    },
    [onDownload]
  );
  const handleDelete = useCallback(
    (attachment: File | Attachment, index: number) => {
      if (onDelete) {
        onDelete(attachment, index);
      }
    },
    [onDelete]
  );
  const handlePreview = useCallback(
    (attachment: File | Attachment) => {
      if (onPreview) {
        onPreview(attachment);
      } else if (
        isFileObject(attachment) &&
        attachment.type.startsWith("image/")
      ) {
        const url = URL.createObjectURL(attachment);
        window.open(url, "_blank");
        URL.revokeObjectURL(url);
      } else if (!isFileObject(attachment) && attachment.url) {
        window.open(attachment.url, "_blank");
      }
    },
    [onPreview]
  );
  const baseClasses = `attachment-component attachment-component-${variant} attachment-component-${theme}`;
  const containerClasses = `${baseClasses} ${className}`.trim();
  return (
    <div className={containerClasses}>
      {allowUpload && (
        <div className="attachment-upload-section mb-3">
          <div
            className={`drag-drop-zone ${isDragOver ? "drag-over" : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="drag-drop-content">
              <i className="ti ti-upload fs-2 text-muted mb-2" />
              <p className="drag-drop-text mb-2">{dragDropText}</p>
              <small className="text-muted">
                Max {maxFiles} files, {formatFileSize(maxFileSize)} each
              </small>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={acceptedFileTypes.join(",")}
              onChange={(e) => handleFileSelect(e.target.files)}
              className="d-none"
            />
          </div>
        </div>
      )}
      {selectedFiles.length > 0 && (
        <div className="selected-files-section mb-3">
          <h6 className="mb-2">Selected Files ({selectedFiles.length})</h6>
          {selectedFiles.map((file, index) => (
            <div key={index} className="selected-file-item">
              <div className="file-info">
                <i className={`${getFileIcon(file.name, file.type)} me-2`} />
                <span className="file-name" title={file.name}>
                  {file.name.length > 30
                    ? file.name.substring(0, 30) + "..."
                    : file.name}
                </span>
                {showFileSize && (
                  <span className="file-size ms-2 text-muted">
                    ({formatFileSize(file.size)})
                  </span>
                )}
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => removeSelectedFile(index)}
                title="Remove file"
              >
                <i className="ti ti-x" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
export default AttachmentComponent;